import {
  createAsyncThunk,
  createSlice,
  isPending,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from '.';
import * as UserAction from '../actions/UserAction';
import {
  User,
  AgreementData,
  CreditCardData,
  LoginData,
  CreditCardResponseData,
  AgreementsResponseData,
  UserResponseData,
  UserData,
  NewUserData,
  AuthUserResponseData,
  LogoutResponseData,
  Contract,
} from '../actions/UserAction';
import {
  createAxiosThunk,
  getActionErrorMessage,
  ThunkStatus,
} from '../AxiosThunk';
import { isReducerAction } from './helpers';
import * as Sentry from '@sentry/react';
import { Stripe, StripeCardElement } from '@stripe/stripe-js';

export type AuthStatus = 'failed' | 'succeeded' | 'pending';

export type ValidFlags = keyof FeatureFlag;

export type FeatureFlag = {
  preferred_driver: boolean;
  accessorials: boolean;
  edit_live_match: boolean;
  show_dropoff_at: boolean;
};

export type UserState = {
  user: null | User;
  contracts: Contract[];
  creditCard?: null | string;
  creditCardError: null | string;
  creditCardStatus: ThunkStatus;
  status: ThunkStatus;
  authStatus: AuthStatus;
  errors: null | string;
  featureFlags: FeatureFlag;
  company: UserAction.Company | null;
};

const initialState: UserState = {
  user: null,
  contracts: [],
  errors: null,
  creditCard: undefined,
  creditCardError: null,
  status: 'idle',
  authStatus: 'pending',
  creditCardStatus: 'idle',
  featureFlags: {
    preferred_driver: false,
    accessorials: false,
    edit_live_match: true,
    show_dropoff_at: false,
  },
  company: null,
};

export const fetchUserContracts =
  createAxiosThunk<UserAction.ContractsResponseData>(
    'user/fetchUserContracts',
    UserAction.getUserContracts
  );

export const fetchUser = createAxiosThunk<UserResponseData>(
  'user/fetchUser',
  UserAction.getUser
);

export const updateUser = createAxiosThunk<UserResponseData, Partial<UserData>>(
  'user/updateUser',
  UserAction.updateUser
);

export const registerUser = createAxiosThunk<
  AuthUserResponseData,
  Partial<NewUserData>
>('user/registerUser', UserAction.registerUser);

export const loginUser = createAxiosThunk<AuthUserResponseData, LoginData>(
  'user/loginUser',
  UserAction.loginUser
);

export const updateUserAgreements = createAxiosThunk<
  AgreementsResponseData,
  AgreementData[]
>('user/updateUserAgreements', UserAction.updateUserAgreements);

export const fetchCreditCard = createAxiosThunk<CreditCardResponseData>(
  'user/fetchCreditCard',
  UserAction.getCreditCard
);

export const updateCreditCard = createAxiosThunk<
  CreditCardResponseData,
  CreditCardData
>('user/updateCreditCard', UserAction.updateCreditCard);

export const logoutUser = createAxiosThunk<LogoutResponseData>(
  'user/logoutUser',
  UserAction.logoutUser
);

export const fetchFeatureFlag = createAxiosThunk<
  UserAction.FeatureFlagResponseData,
  UserAction.FeatureFlagParams
>('user/featureFlags', UserAction.getFeatureFlag);

export const createStripeToken = createAsyncThunk(
  'user/createStripeToken',
  async (
    {
      stripe,
      cardElement,
      user,
    }: { stripe: Stripe; cardElement: StripeCardElement; user: User },
    { rejectWithValue }
  ) => {
    const { token, error } = await stripe.createToken(cardElement, {
      name: `${user.first_name} ${user.last_name}`,
    });

    if (error) {
      return rejectWithValue(error.message);
    }

    return token;
  }
);

const userSuccess = (
  state: UserState,
  action: PayloadAction<UserResponseData | AuthUserResponseData, string>
) => {
  const response = action.payload.response;
  state.user = 'shipper' in response ? response.shipper : response;
  state.status = 'succeeded';
  state.authStatus = 'succeeded';
  state.errors = null;
  if ('shipper' in response) {
    Sentry.setUser({
      id: response.shipper.id,
      username: `${response.shipper.first_name} ${response.shipper.last_name}`,
      email: response.shipper.email,
    });
  }
};

const userFailure = (state: UserState) => {
  state.user = null;
  state.status = 'failed';
  state.authStatus = 'failed';
  state.creditCard = null;
};

const authFailure = (
  state: UserState,
  action: PayloadAction<unknown, string>
) => {
  userFailure(state);
  state.errors = getActionErrorMessage(action);
};

const updateFailure = (
  state: UserState,
  action: PayloadAction<unknown, string>
) => {
  state.errors = getActionErrorMessage(action);
};

const reducerName = 'user';

const isUserPending = (action: PayloadAction<unknown>) =>
  isReducerAction(reducerName)(action) && isPending(action);

const userSlice = createSlice({
  name: reducerName,
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(registerUser.fulfilled, userSuccess)
      .addCase(registerUser.rejected, authFailure)
      .addCase(loginUser.fulfilled, userSuccess)
      .addCase(loginUser.rejected, authFailure)
      .addCase(fetchUser.fulfilled, userSuccess)
      .addCase(fetchUser.rejected, userFailure)
      .addCase(logoutUser.fulfilled, userFailure)
      .addCase(logoutUser.rejected, userFailure)
      .addCase(updateUser.fulfilled, userSuccess)
      .addCase(updateUser.rejected, updateFailure)
      .addCase(updateUserAgreements.fulfilled, (state, action) => {
        state.user = state.user && {
          ...state.user,
          pending_agreements: action.payload.agreement_documents,
        };
      })
      .addCase(updateUserAgreements.rejected, updateFailure)
      .addCase(fetchCreditCard.fulfilled, (state, action) => {
        state.creditCard = action.payload.response?.credit_card || null;
      })
      .addCase(fetchCreditCard.rejected, (state, action) => {
        state.creditCard = null;
        updateFailure(state, action);
      })
      .addCase(updateCreditCard.fulfilled, (state, action) => {
        state.creditCard = action.payload.response?.credit_card || null;
        state.creditCardStatus = 'succeeded';
      })
      .addCase(updateCreditCard.pending, state => {
        state.creditCardStatus = 'loading';
        state.creditCardError = null;
      })
      .addCase(updateCreditCard.rejected, state => {
        state.creditCardError = 'Please recheck your credit card information.';
        state.creditCardStatus = 'failed';
      })
      .addCase(createStripeToken.fulfilled, state => {
        state.creditCardError = null;
        state.creditCardStatus = 'succeeded';
      })
      .addCase(createStripeToken.pending, state => {
        state.creditCardStatus = 'loading';
        state.creditCardError = null;
      })
      .addCase(createStripeToken.rejected, (state, action) => {
        state.creditCardError = action.payload as string;
        state.creditCardStatus = 'failed';
      })
      .addCase(fetchFeatureFlag.fulfilled, (state, action) => {
        const key = action.meta.arg.flag as ValidFlags;
        const value = action.payload.enabled;
        state.featureFlags[key] = !!value;
      })
      .addCase(fetchFeatureFlag.rejected, updateFailure)
      .addCase(fetchUserContracts.fulfilled, (state, action) => {
        state.contracts = action.payload.data;
      })
      .addCase(fetchUserContracts.rejected, updateFailure)
      .addMatcher(isUserPending, state => {
        state.status = 'loading';
      });
  },
});

export default userSlice.reducer;

export const selectFeatureFlag = (flag: ValidFlags) => (state: RootState) =>
  state.user.featureFlags[flag];
export const selectUserContracts = (state: RootState) => state.user.contracts;
export const selectUserContract =
  (contract_id: string | null | undefined) => (state: RootState) =>
    (contract_id && state.user.contracts.find(c => c.id === contract_id)) ||
    null;
export const selectUser = (state: RootState) => state.user.user;
export const selectUserStatus = (state: RootState) => state.user.status;
export const selectUserErrors = (state: RootState) => state.user.errors;
export const selectAuthStatus = (state: RootState) => state.user.authStatus;
export const selectCreditCard = (state: RootState) => state.user.creditCard;
export const selectCreditCardError = (state: RootState) =>
  state.user.creditCardError;
export const selectCreditCardStatus = (state: RootState) =>
  state.user.creditCardStatus;
